import { useEffect } from 'react';

import { useQueryState } from 'hooks/useQueryState';

import { useConfigurationStore } from 'store/configurationsStore';

import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { BreakPointMap } from 'components/Dashboards/Reporting/reporting.types';

export const ScrollToBottomUrlQueryValues = {
    scrollToWidget: 'scrollToWidget',
    componentKey: 'componentKey',
} as const;

interface UseScrollToBottomProps {
    layout: BreakPointMap | null;
}

/**
 * Custom hook that handles scrolling to the bottom when a new widget is added to the report.
 * Also manages URL parameter cleanup after scroll.
 *
 * Check if possible to work with react router state instead of query params for cleaner solution.
 */
export const useScrollToBottomOfReport = ({ layout }: UseScrollToBottomProps) => {
    const configurationStore = useConfigurationStore();
    const [scrollToWidgetId, setScrollToWidgetId] = useQueryState<string>(ScrollToBottomUrlQueryValues.scrollToWidget);
    const [componentKey, setComponentKey] = useQueryState<string>(ScrollToBottomUrlQueryValues.componentKey);

    const WIDGETS_TO_IGNORE_SCROLL = [
        configurationStore.automatedNarrative.componentKey,
        configurationStore.reportSummary.componentKey,
    ];

    const cleanUpUrlParams = () => {
        setScrollToWidgetId(undefined);
        setComponentKey(undefined);
    };

    useEffect(() => {
        if (scrollToWidgetId && componentKey && layout) {
            // Check if componentKey should trigger scroll behavior
            if (!WIDGETS_TO_IGNORE_SCROLL.includes(componentKey as keyof IHashmap)) {
                // Wait for the DOM to be ready and components to render
                const timer = setTimeout(() => {
                    const widgetElement = document.querySelector(`[data-report-widget-id="${scrollToWidgetId}"]`);
                    if (widgetElement) {
                        widgetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        cleanUpUrlParams();
                    }
                }, 1000); // Small delay to ensure widgets are rendered

                return () => clearTimeout(timer);
            } else {
                // Clean up the URL params even if we don't scroll
                cleanUpUrlParams();
            }
        }
    }, [scrollToWidgetId, componentKey, layout]);
};
